import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { config } from '../../config/config';
import { DEFAULT_PROPOSAL_TEMPLATE, generateItemsHtml } from '../../data/defaultProposalTemplate';
import { resizeImage } from '../../utils/imageUtils';
import Dialog from '../common/Dialog';
import html2pdf from 'html2pdf.js';

// Add styles for mobile compatibility
const mobileStyles = `
  .signature-canvas {
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  .touch-target-size {
    min-height: 44px;
    min-width: 44px;
  }
  
  @media (max-width: 768px) {
    .proposal-content {
      font-size: 14px;
      line-height: 1.5;
    }
    
    .proposal-content table {
      font-size: 12px;
    }
    
    .proposal-content h1 {
      font-size: 18px;
    }
    
    .proposal-content h2 {
      font-size: 16px;
    }
    
    .proposal-content h3 {
      font-size: 14px;
    }
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = mobileStyles;
  if (!document.head.querySelector('style[data-mobile-proposal]')) {
    styleElement.setAttribute('data-mobile-proposal', 'true');
    document.head.appendChild(styleElement);
  }
}

const FloatingHeader = ({ onPrint }) => (
  <div className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3 md:py-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">Proposal Review</h1>
          <p className="mt-1 md:mt-2 text-xs md:text-sm text-gray-600">
            Please review the proposal details below and provide your signature at the bottom to approve.
          </p>
        </div>
        <button
          onClick={onPrint}
          className="flex-shrink-0 px-3 md:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center justify-center gap-2 text-sm md:text-base"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
          </svg>
          <span className="hidden sm:inline">Print PDF</span>
          <span className="sm:hidden">Print</span>
        </button>
      </div>
    </div>
  </div>
);

const ConfirmationDialog = ({ isOpen, onClose, title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel', disableConfirm = false, disableCancel = false }) => {
  if (!isOpen) return null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-4">
          <button
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            onClick={onClose}
            disabled={disableCancel}
          >
            {cancelText}
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            disabled={disableConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

const StepsGuide = () => {
  const scrollToSignature = () => {
    const signatureSection = document.getElementById('signature-section');
    if (signatureSection) {
      signatureSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="fixed right-8 top-32 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Steps to Sign</h3>
      <ol className="space-y-4">
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            1
          </span>
          <span className="text-sm text-gray-600">
            Review the proposal details carefully
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            2
          </span>
          <button 
            onClick={scrollToSignature}
            className="text-sm text-blue-600 hover:text-blue-800 text-left"
          >
            Click here to go to the signature section
          </button>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            3
          </span>
          <span className="text-sm text-gray-600">
            Enter your full name in the name field
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            4
          </span>
          <span className="text-sm text-gray-600">
            Verify the date is correct
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            5
          </span>
          <span className="text-sm text-gray-600">
            Draw your signature in the signature box
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
            6
          </span>
          <span className="text-sm text-gray-600">
            Click "Approve Proposal" to complete
          </span>
        </li>
      </ol>
    </div>
  );
};

const formatCurrency = (amount) => {
  return Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const ProposalView = () => {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const signatureRef = useRef();
  const proposalContentRef = useRef();
  const [error, setError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmReject, setShowConfirmReject] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProposal();
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}/public`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch proposal');
      }
      
      setProposal(data.proposal);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderProposalTemplate = () => {
    if (!proposal) return '';

    const lead = proposal.lead || {};
    const today = new Date().toLocaleDateString();

    // Format the complete address by concatenating all address fields
    const formatAddress = (address) => {
      if (!address) return '';
      const parts = [
        address.street,
        address.city,
        address.state,
        address.zipCode
      ].filter(Boolean);
      return parts.join(', ');
    };

    // Get client name from various possible locations
    const clientName = lead.siteContact?.name || 
                      lead.client?.name || 
                      lead.leadContact?.name || 
                      '';

    // Only use the site contact address (job address)
    const address = lead.siteContact?.address || {};
    
    const formattedAddress = formatAddress(address);

    // Get display settings with defaults
    const displaySettings = proposal.displaySettings || {
      showItem: true,
      showQuantity: false,
      showUnit: false,
      showPrice: false,
      showCostCodes: false,
      showScopeDescription: true,
      showCostCodeDescription: false,
      expandGroups: false,
      separateGroups: false,
      showTotalCost: true
    };

    // Process scope items with their cost codes - they're already properly structured
    const scopeItems = proposal.scopeOfWork || [];

    // Process standalone cost codes
    const standaloneCostCodes = proposal.costCodes || [];

    // Combine all items
    const allItems = [...scopeItems, ...standaloneCostCodes];

    // Generate items HTML using the template function
    const itemsHtml = generateItemsHtml(allItems, displaySettings);

    // Generate custom signature section if it exists
    // This won't be directly used in the template, but will be injected via JavaScript after rendering
    const signatureSection = proposal.signature ? `
      <div class="mt-8 border-t pt-8">
        <p>Signed by: ${proposal.signature.name}</p>
        <p>Date: ${new Date(proposal.signature.date).toLocaleDateString()}</p>
        ${proposal.signature.signatureImage ? `
          <img src="${proposal.signature.signatureImage}" alt="Signature" class="mt-4 h-20" />
        ` : ''}
      </div>
    ` : '';

    // Get client email and determine if it's multiple emails
    const clientEmail = lead.client?.email || lead.clientEmail || lead.leadContact?.email || '';
    const hasMultipleEmails = clientEmail.includes(',');
    const emailLabel = hasMultipleEmails ? 'Emails' : 'Email';
    
    // Create template data object with updated fields
    const templateData = {
      'lead.clientName': clientName,
      'lead.clientEmail': clientEmail,
      'lead.clientEmailLabel': emailLabel,
      'lead.streetAddress': formattedAddress,
      'lead.city': '', // We'll use the full address above instead
      'lead.state': '',
      'lead.zipCode': '',
      'proposal.title': proposal.title || `Proposal for ${clientName}`,
      'today': today,
      'itemsHtml': itemsHtml,
      'totalClientPrice.toFixed(2)': formatCurrency(proposal.totals?.clientPrice || 0),
      'signatureSection': '' // We'll handle the signature insertion with JavaScript
    };

    // Replace all template variables2
    let html = DEFAULT_PROPOSAL_TEMPLATE;
    Object.entries(templateData).forEach(([key, value]) => {
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\$\\{${escapedKey}\\}`, 'g');
      html = html.replace(regex, value || '');
    });

    // Format any remaining currency values in the HTML
    html = html.replace(/\$(\d+\.\d{2})/g, (match, amount) => {
      return `$${formatCurrency(parseFloat(amount))}`;
    });

    return html;
  };

  const handleApprove = async () => {
    if (!name || !signatureRef.current.toData().length) {
      setErrorMessage('Please provide your name and signature');
      setShowErrorDialog(true);
      return;
    }

    try {
      setIsApproving(true);
      
      const originalSignature = signatureRef.current.toDataURL();
      
      const compressedSignature = await resizeImage(originalSignature, {
        maxWidth: 600,
        maxHeight: 300,
        quality: 0.5,
        type: 'image/jpeg'
      });

      const signatureData = {
        name,
        date,
        signature: compressedSignature
      };

      // Before submitting, let's also update the signature fields in the template
      if (proposalContentRef.current) {
        const signatureFields = proposalContentRef.current.querySelectorAll('.border-b.border-gray-300.pb-2');
        
        if (signatureFields.length >= 3) {
          // Insert signature image
          const signatureField = signatureFields[0];
          const signatureImg = document.createElement('img');
          signatureImg.src = compressedSignature;
          signatureImg.alt = 'Signature';
          signatureImg.className = 'h-16';
          signatureField.querySelector('div').innerHTML = '';
          signatureField.querySelector('div').appendChild(signatureImg);
          
          // Insert date
          const dateField = signatureFields[1];
          dateField.querySelector('div').textContent = new Date(date).toLocaleDateString();
          
          // Insert name
          const nameField = signatureFields[2];
          nameField.querySelector('div').textContent = name;
        }
      }

      const response = await fetch(`${config.apiUrl}/api/proposals/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(signatureData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message);
      }

      // Update only the signature and status from the response
      setProposal(prev => ({
        ...prev,
        status: data.proposal.status,
        signature: data.proposal.signature
      }));
      
      setShowSuccessDialog(true);

    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorDialog(true);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsRejecting(true);
      
      const response = await fetch(`${config.apiUrl}/api/proposals/${id}/reject`, {
        method: 'POST'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message);
      }

      // Update only the status
      setProposal(prev => ({
        ...prev,
        status: data.proposal.status
      }));

      setShowSuccessDialog(true);
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorDialog(true);
    } finally {
      setIsRejecting(false);
    }
  };

  // Add a function to display existing signature if it exists
  const renderExistingSignature = () => {
    if (proposal?.signature?.signatureImage) {
      return (
        <div className="mt-4">
          <p className="text-sm text-gray-600">Signed by {proposal.signature.name}</p>
          <p className="text-sm text-gray-600">
            on {new Date(proposal.signature.date).toLocaleDateString()}
          </p>
          <img 
            src={proposal.signature.signatureImage} 
            alt="Signature" 
            className="mt-2 border border-gray-300 rounded-md"
          />
        </div>
      );
    }
    return null;
  };

  const handlePrint = async () => {
    if (!proposalContentRef.current || isPrinting) return;

    setIsPrinting(true);

    // Insert signature into the template before printing
    const element = proposalContentRef.current;
    
    // Find the signature fields in the template
    const signatureFields = element.querySelectorAll('.border-b.border-gray-300.pb-2');
    
    if (signatureFields.length >= 3 && proposal.signature) {
      // Insert signature image
      if (proposal.signature.signatureImage) {
        const signatureField = signatureFields[0];
        const signatureImg = document.createElement('img');
        signatureImg.src = proposal.signature.signatureImage;
        signatureImg.alt = 'Signature';
        signatureImg.className = 'h-16';
        signatureField.querySelector('div').innerHTML = '';
        signatureField.querySelector('div').appendChild(signatureImg);
      }
      
      // Insert date
      const dateField = signatureFields[1];
      dateField.querySelector('div').textContent = new Date(proposal.signature.date).toLocaleDateString();
      
      // Insert name
      const nameField = signatureFields[2];
      nameField.querySelector('div').textContent = proposal.signature.name;
    }

    const opt = {
      margin: [10, 10],
      filename: `proposal-${id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setIsPrinting(false);
      
      // Reset any changes we made for printing
      if (proposal.signature) {
        element.querySelectorAll('.border-b.border-gray-300.pb-2').forEach(field => {
          field.querySelector('div').innerHTML = '';
        });
      }
    }
  };

  // Add this function to insert signature into template fields after component renders
  useEffect(() => {
    // Only run after the proposal is loaded and if there's a signature
    if (proposal && proposal.signature && proposalContentRef.current) {
      const element = proposalContentRef.current;
      const signatureFields = element.querySelectorAll('.border-b.border-gray-300.pb-2');
      
      if (signatureFields.length >= 3) {
        // Insert signature image
        if (proposal.signature.signatureImage) {
          const signatureField = signatureFields[0];
          const signatureImg = document.createElement('img');
          signatureImg.src = proposal.signature.signatureImage;
          signatureImg.alt = 'Signature';
          signatureImg.className = 'h-16';
          signatureField.querySelector('div').innerHTML = '';
          signatureField.querySelector('div').appendChild(signatureImg);
        }
        
        // Insert date
        const dateField = signatureFields[1];
        dateField.querySelector('div').textContent = new Date(proposal.signature.date).toLocaleDateString();
        
        // Insert name
        const nameField = signatureFields[2];
        nameField.querySelector('div').textContent = proposal.signature.name;
      }
    }
  }, [proposal]);

  // Add this effect to prefill the date when component mounts
  useEffect(() => {
    // Prefill the date field with today's date
    setDate(new Date().toISOString().split('T')[0]);
  }, []);

  // Add click handlers to images in the proposal content
  useEffect(() => {
    if (proposalContentRef.current) {
      const images = proposalContentRef.current.querySelectorAll('img');
      
      const handleImageClick = (e) => {
        // Don't open lightbox for signature images
        if (e.target.alt === 'Signature') return;
        setSelectedImage(e.target.src);
      };

      images.forEach(img => {
        img.style.cursor = 'pointer';
        img.addEventListener('click', handleImageClick);
      });

      // Cleanup
      return () => {
        images.forEach(img => {
          img.removeEventListener('click', handleImageClick);
        });
      };
    }
  }, [proposal]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!proposal) return <div>Proposal not found</div>;

  return (
    <>
      <FloatingHeader onPrint={handlePrint} />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8 mt-20 md:mt-24">
        {/* Main Content - Responsive layout */}
        <div className="lg:grid lg:grid-cols-[1fr,320px] lg:gap-8 space-y-6 lg:space-y-0">
          {/* Left Column - Proposal Content */}
          <div className="space-y-6 md:space-y-8">
            {/* Proposal Content */}
            <div ref={proposalContentRef} className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 md:p-6 proposal-content">
                <div dangerouslySetInnerHTML={{ __html: renderProposalTemplate() }} />
              </div>
            </div>

            {/* Signature Section - only show if proposal isn't already approved/rejected */}
            {proposal && !proposal.status.includes('approved') && !proposal.status.includes('rejected') && (
            <div id="signature-section" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Sign to Approve</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Your signature will be placed in the signature field in the document above.
                </p>
                
                <div className="space-y-4 md:space-y-6">
                  {/* Name Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2.5 md:py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Date Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full rounded-md border border-gray-300 shadow-sm py-2.5 md:py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
                    />
                  </div>

                  {/* Signature Canvas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Signature</label>
                    <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'signature-canvas w-full h-32 md:h-40 touch-action-none'
                        }}
                      />
                    </div>
                    <button
                      onClick={() => signatureRef.current.clear()}
                      className="mt-2 text-sm text-gray-600 hover:text-gray-800 touch-target-size p-1"
                    >
                      Clear Signature
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-6 md:mt-8">
                    <button
                      onClick={handleApprove}
                      disabled={isApproving}
                      className="flex-1 bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed flex justify-center items-center text-base md:text-sm font-medium"
                    >
                      {isApproving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : "Approve Proposal"}
                    </button>
                    <button
                      onClick={() => setShowConfirmReject(true)}
                      disabled={isRejecting}
                      className="flex-1 bg-white text-gray-700 px-4 py-3 md:py-2 rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex justify-center items-center text-base md:text-sm font-medium"
                    >
                      {isRejecting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : "Reject Proposal"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show approval details if proposal is already approved */}
            {proposal && proposal.status === 'customer_approved' && (
              <div id="signature-section" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Approval Details</h2>
                <h3 className="text-lg font-medium text-green-600">Proposal Approved</h3>
                {renderExistingSignature()}
                </div>
              )}
            
            {/* Show rejection message if proposal is rejected */}
            {proposal && proposal.status === 'customer_rejected' && (
              <div id="signature-section" className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Proposal Status</h2>
                <h3 className="text-lg font-medium text-red-600">Proposal Rejected</h3>
            </div>
            )}
          </div>

          {/* Right Column - Steps Guide - Hidden on mobile, shown on larger screens */}
          {!proposal?.status?.includes('approved') && !proposal?.status?.includes('rejected') && (
            <>
              {/* Mobile Steps Guide - Shown inline on mobile */}
              <div className="lg:hidden bg-white rounded-lg shadow-lg border border-gray-200 p-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Steps to Sign</h3>
                <ol className="space-y-3">
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      1
                    </span>
                    <span className="text-sm text-gray-600">
                      Review the proposal details carefully
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      2
                    </span>
                    <button 
                      onClick={() => {
                        const signatureSection = document.getElementById('signature-section');
                        if (signatureSection) {
                          signatureSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 text-left"
                    >
                      Tap here to go to the signature section
                    </button>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      3
                    </span>
                    <span className="text-sm text-gray-600">
                      Enter your full name in the name field
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      4
                    </span>
                    <span className="text-sm text-gray-600">
                      Verify the date is correct
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      5
                    </span>
                    <span className="text-sm text-gray-600">
                      Draw your signature in the signature box
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                      6
                    </span>
                    <span className="text-sm text-gray-600">
                      Tap "Approve Proposal" to complete
                    </span>
                  </li>
                </ol>
              </div>

              {/* Desktop Steps Guide - Sticky sidebar on larger screens */}
              <div className="hidden lg:block h-fit sticky top-32">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Steps to Sign</h3>
                  <ol className="space-y-4">
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        1
                      </span>
                      <span className="text-sm text-gray-600">
                        Review the proposal details carefully
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        2
                      </span>
                      <button 
                        onClick={() => {
                          const signatureSection = document.getElementById('signature-section');
                          if (signatureSection) {
                            signatureSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 text-left"
                      >
                        Click here to go to the signature section
                      </button>
                    </li>
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        3
                      </span>
                      <span className="text-sm text-gray-600">
                        Enter your full name in the name field
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        4
                      </span>
                      <span className="text-sm text-gray-600">
                        Verify the date is correct
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        5
                      </span>
                      <span className="text-sm text-gray-600">
                        Draw your signature in the signature box
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mr-3 shrink-0">
                        6
                      </span>
                      <span className="text-sm text-gray-600">
                        Click "Approve Proposal" to complete
                      </span>
                    </li>
                  </ol>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dialogs remain unchanged */}
      <ConfirmationDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Success"
        message="Operation completed successfully!"
        confirmText="OK"
        onConfirm={() => setShowSuccessDialog(false)}
      />

      <ConfirmationDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        onConfirm={() => setShowErrorDialog(false)}
      />

      <ConfirmationDialog
        isOpen={showConfirmReject}
        onClose={() => !isRejecting && setShowConfirmReject(false)}
        title="Confirm Rejection"
        message="Are you sure you want to reject this proposal?"
        confirmText={isRejecting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : "Reject"}
        cancelText="Cancel"
        onConfirm={handleReject}
        disableConfirm={isRejecting}
        disableCancel={isRejecting}
      />

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Close image"
          >
            <svg 
              className="w-8 h-8 md:w-10 md:h-10" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
          <img
            src={selectedImage}
            alt="Full size view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ProposalView; 